<script setup lang="ts">
import type { NuxtError } from '#app'
import * as Sentry from '@sentry/nuxt'
import { darkTheme } from 'naive-ui'
import { themeOverrides } from './theme.config'

const props = defineProps({
  error: Object as () => NuxtError,
})

const errorTitle = computed(() => {
  switch (props.error?.statusCode) {
    case 403:
      return 'Fehler 403: Nicht berechtigt'
    case 404:
      return 'Fehler 404: Seite existiert nicht'
    default:
      return `Fehler ${`${props.error?.statusCode}: ` || ''}Unerwarteter Fehler`
  }
})

const errorDescription = computed(() => {
  switch (props.error?.statusCode) {
    case 403:
      return 'Du scheinst eine Ressource angefordert zu haben, auf die Du keinen Zugriff hast.'
    case 404:
      return 'Die angeforderte Seite scheint nicht zu existieren.'
    default:
      return `Wir wissen selbst nicht genau, wie es zu diesem Fehler kommen konnte. Bitte versuche es später erneut.`
  }
})

useHead({
  title: `${errorTitle.value}`,
})

const handleError = () => clearError({ redirect: '/' })

async function openSentry() {
  const feedback = Sentry.feedbackIntegration({ autoInject: false })
  const form = await feedback.createForm()
  form.appendToDom()
  form.open()
}

const { colorMode, colorModePreference } = useNaiveColorMode()

const theme = ref<typeof darkTheme | null>(null)

watch(colorMode, () => theme.value = colorMode.value === 'dark' ? darkTheme : null, { immediate: true })

const colorModeNuxt = useColorMode()

watch(() => colorModeNuxt.value, (newValue) => {
  if (newValue === 'dark') {
    colorModePreference.set('dark')
    return
  }
  colorModePreference.set('light')
}, { immediate: true })
</script>

<template>
  <NaiveConfig :theme-overrides="themeOverrides" :theme="theme">
    <NLoadingBarProvider>
      <NaiveLoadingBar navigation />
    </NLoadingBarProvider>
    <NLayout>
      <NElement class="h-screen w-screen flex flex-col items-center justify-center pa-5 space-y-8">
        <UColorModeSwitch class="absolute top-5 right-5" />
        <div class="flex items-center space-x-4">
          <img
            src="/pwa-192x192.png"
            alt="trackIT Logo"
            title="trackIT"
            class="w-20"
          >
          <span class="text-3xl font-semibold">trackIT</span>
        </div>
        <NCard
          size="huge"
          class="max-w-2xl w-full"
        >
          <template #header>
            <div class="text-4xl">
              {{ errorTitle }}
            </div>
          </template>
          <div class="space-y-6">
            <section class="text-lg line-height-relaxed">
              <span class="align-middle">{{ errorDescription }}</span> <Icon class="align-middle text-2xl" name="i-mdi-emoticon-cry-outline" />
              <p class="mt-4">
                Wenn Du denkst, dass es dabei nicht mit rechten Dingen zugeht, melde uns das bitte. Halte dazu bitte möglichst genau fest, wie es zu dem Fehler gekommen ist.
              </p>
            </section>
            <NSpace class="justify-self-end !justify-end sm:justify-start sm:items-center">
              <NButton
                text primary
                size="large"
                @click="handleError"
              >
                <template #icon>
                  <Icon name="i-ic-baseline-check" />
                </template>
                Verstanden, zurück zur Seite!
              </NButton>
              <NButton
                tertiary
                type="error"
                size="large"
                @click="openSentry"
              >
                <template #icon>
                  <Icon name="i-fa6-solid-bullhorn" />
                </template>
                Einen Fehler melden
              </NButton>
            </NSpace>
          </div>
        </NCard>
      </NElement>
    </NLayout>
  </NaiveConfig>
</template>
